import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
} from '@mui/material';
import {
  ContentCut as ServicesIcon,
  LocalOffer as PackagesIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

const sections = [
  {
    id: 'services',
    title: 'Services',
    description:
      'Manage your service menu, durations, pricing, patch test requirements, and rebook intervals.',
    icon: <ServicesIcon sx={{ fontSize: 32 }} />,
    iconBg: 'linear-gradient(135deg, #00ACC1, #00838F)',
    color: '#00ACC1',
    path: '/pro/services',
    buttonLabel: 'Open Services',
  },
  {
    id: 'packages',
    title: 'Packages',
    description:
      'Bundle services together into packages with fixed or client-choice slots, price overrides, and deposit rules.',
    icon: <PackagesIcon sx={{ fontSize: 32 }} />,
    iconBg: 'linear-gradient(135deg, #7B68EE, #5A4FCF)',
    color: '#7B68EE',
    path: '/pro/packages',
    buttonLabel: 'Open Packages',
  },
];

const ProServicesHub = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f7fa',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: 8,
        px: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6, maxWidth: 560 }}>
        <Typography variant="h4" fontWeight={700} color="#2c3e50" gutterBottom>
          Services &amp; Packages
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your service menu and create bundled packages for your clients to book online.
        </Typography>
      </Box>

      {/* Cards */}
      <Box
        sx={{
          display: 'flex',
          gap: 4,
          flexWrap: 'wrap',
          justifyContent: 'center',
          width: '100%',
          maxWidth: 780,
        }}
      >
        {sections.map((section) => (
          <Card
            key={section.id}
            elevation={0}
            sx={{
              width: 340,
              border: '1px solid #e0e0e0',
              borderRadius: 3,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: `0 12px 28px ${section.color}30`,
                borderColor: section.color,
                '& .action-button': { background: section.iconBg },
              },
            }}
          >
            <CardContent
              sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}
            >
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: section.iconBg,
                  boxShadow: `0 4px 12px ${section.color}40`,
                  mb: 3,
                }}
              >
                {section.icon}
              </Avatar>

              <Typography variant="h6" fontWeight={700} color="#2c3e50" gutterBottom>
                {section.title}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.7, flexGrow: 1, mb: 3 }}
              >
                {section.description}
              </Typography>

              <Button
                fullWidth
                variant="contained"
                className="action-button"
                endIcon={<ArrowIcon />}
                onClick={() => navigate(section.path)}
                sx={{
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: '0.85rem',
                  letterSpacing: '0.05em',
                  transition: 'all 0.3s',
                  mt: 'auto',
                  '&:hover': {
                    background: section.iconBg,
                    transform: 'scale(1.02)',
                  },
                }}
              >
                {section.buttonLabel}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default ProServicesHub;